<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">
      <div class="modal-header">
        <h2>Choose Your Plan</h2>
        <button @click="$emit('close')" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <!-- Billing Interval Toggle -->
        <div class="interval-toggle">
          <button
            @click="selectedInterval = 'monthly'"
            :class="{ active: selectedInterval === 'monthly' }"
            class="interval-btn"
          >
            Monthly
          </button>
          <button
            @click="selectedInterval = 'annual'"
            :class="{ active: selectedInterval === 'annual' }"
            class="interval-btn"
          >
            Annual
            <span class="save-badge">Save 17%</span>
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading pricing plans...</p>
        </div>

        <!-- Pricing Plans -->
        <div v-else class="pricing-plans">
          <div
            v-for="plan in plans"
            :key="plan.id"
            class="pricing-card"
            :class="{ 
              featured: plan.id === 'PROFESSIONAL',
              disabled: plan.custom 
            }"
          >
            <!-- Featured Badge -->
            <div v-if="plan.id === 'PROFESSIONAL'" class="featured-badge">
              <i class="fas fa-star"></i> Most Popular
            </div>

            <!-- Plan Header -->
            <div class="plan-header">
              <h3>{{ plan.name }}</h3>
              <div v-if="!plan.custom" class="price">
                <span class="currency">$</span>
                <span class="amount">{{ getPlanPrice(plan) }}</span>
                <span class="period">/{{ selectedInterval === 'monthly' ? 'month' : 'year' }}</span>
              </div>
              <div v-else class="price">
                <span class="custom-text">Custom Pricing</span>
              </div>
              <p v-if="selectedInterval === 'annual' && !plan.custom" class="savings">
                Save ${{ getSavings(plan) }} per year
              </p>
            </div>

            <!-- Features List -->
            <div class="plan-features">
              <ul>
                <li v-for="(feature, index) in plan.features" :key="index">
                  <i class="fas fa-check-circle"></i>
                  <span>{{ feature }}</span>
                </li>
              </ul>
            </div>

            <!-- Select Button -->
            <div class="plan-action">
              <button
                v-if="!plan.custom"
                @click="selectPlan(plan.id)"
                class="btn-select"
                :class="{ 'btn-featured': plan.id === 'PROFESSIONAL' }"
              >
                <i class="fas fa-arrow-right"></i>
                Select {{ plan.name }}
              </button>
              <button
                v-else
                class="btn-select btn-contact"
                @click="contactSales"
              >
                <i class="fas fa-phone"></i>
                Contact Sales
              </button>
            </div>
          </div>
        </div>

        <!-- Trial Information -->
        <div class="trial-info">
          <i class="fas fa-gift"></i>
          <strong>14-day free trial</strong> on all plans. No credit card required to start.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'PricingPlansModal',

  data() {
    return {
      loading: true,
      selectedInterval: 'monthly',
      plans: [],
    };
  },

  async mounted() {
    await this.loadPricingPlans();
  },

  methods: {
    async loadPricingPlans() {
      this.loading = true;
      try {
        const response = await api.get('/users/billing/pricing_plans/');
        this.plans = response.data;
      } catch (error) {
        console.error('Error loading pricing plans:', error);
        this.$toast?.error('Failed to load pricing plans');
      } finally {
        this.loading = false;
      }
    },

    getPlanPrice(plan) {
      if (this.selectedInterval === 'monthly') {
        return plan.monthly_price;
      } else {
        return plan.annual_price;
      }
    },

    getSavings(plan) {
      if (!plan.monthly_price || !plan.annual_price) return 0;
      const monthlyTotal = plan.monthly_price * 12;
      return monthlyTotal - plan.annual_price;
    },

    selectPlan(planId) {
      this.$emit('select', planId, this.selectedInterval);
      this.$emit('close');
    },

    contactSales() {
      // Open email client or redirect to contact page
      window.location.href = 'mailto:sales@praetorianai.com?subject=Enterprise Plan Inquiry';
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
  overflow-y: auto;
}

.modal-container {
  background: white;
  border-radius: 16px;
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary, #1a1a1a);
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.5rem;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #1a1a1a;
}

.modal-body {
  padding: 2rem;
  overflow-y: auto;
}

.interval-toggle {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-bottom: 2rem;
  background: #f3f4f6;
  padding: 0.5rem;
  border-radius: 12px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.interval-btn {
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--text-secondary, #6b7280);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.interval-btn.active {
  background: white;
  color: var(--primary-color, #4f46e5);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.save-badge {
  background: #10b981;
  color: white;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
}

.loading-state {
  text-align: center;
  padding: 4rem 2rem;
}

.spinner {
  border: 3px solid #f3f4f6;
  border-top: 3px solid var(--primary-color, #4f46e5);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.pricing-plans {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
}

.pricing-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.3s;
}

.pricing-card:hover:not(.disabled) {
  border-color: var(--primary-color, #4f46e5);
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(79, 70, 229, 0.15);
}

.pricing-card.featured {
  border-color: var(--primary-color, #4f46e5);
  border-width: 3px;
}

.pricing-card.disabled {
  opacity: 0.8;
}

.featured-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.375rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 700;
  white-space: nowrap;
}

.plan-header {
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e5e7eb;
}

.plan-header h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: var(--text-primary, #1a1a1a);
}

.price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.currency {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-secondary, #6b7280);
}

.amount {
  font-size: 3rem;
  font-weight: 800;
  color: var(--text-primary, #1a1a1a);
  line-height: 1;
}

.period {
  font-size: 1rem;
  color: var(--text-secondary, #6b7280);
  font-weight: 500;
}

.custom-text {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color, #4f46e5);
}

.savings {
  color: #10b981;
  font-size: 0.875rem;
  font-weight: 600;
  margin-top: 0.5rem;
}

.plan-features {
  flex: 1;
  margin-bottom: 2rem;
}

.plan-features ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.plan-features li {
  padding: 0.75rem 0;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: var(--text-secondary, #6b7280);
}

.plan-features li i {
  color: #10b981;
  margin-top: 0.25rem;
  flex-shrink: 0;
}

.plan-action {
  margin-top: auto;
}

.btn-select {
  width: 100%;
  padding: 1rem;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--primary-color, #4f46e5);
  color: white;
}

.btn-select:hover {
  background: #4338ca;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
}

.btn-featured {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.btn-featured:hover {
  background: linear-gradient(135deg, #5568d3 0%, #65408b 100%);
}

.btn-contact {
  background: #f3f4f6;
  color: var(--text-primary, #1a1a1a);
}

.btn-contact:hover {
  background: #e5e7eb;
  box-shadow: none;
}

.trial-info {
  text-align: center;
  padding: 1.5rem;
  background: #f0fdf4;
  border: 2px solid #86efac;
  border-radius: 12px;
  color: #166534;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.trial-info i {
  color: #10b981;
  font-size: 1.25rem;
}

@media (max-width: 768px) {
  .modal-container {
    max-height: 95vh;
  }

  .modal-header {
    padding: 1.5rem;
  }

  .modal-header h2 {
    font-size: 1.25rem;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .pricing-plans {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .interval-toggle {
    flex-direction: column;
  }

  .amount {
    font-size: 2.5rem;
  }

  .trial-info {
    flex-direction: column;
    text-align: center;
  }
}
</style>

